import {
    getCoatingWeights,
    getCompliances,
    getFinishes,
    getGradeFamily,
    getGrades,
    getProducts,
    getSpecifications,
    getSurfaceFinishes,
    getSurfaceTreatments,
    getTempers,
    ListingBuyerModel,
    MaterialProperties,
    sortCoatingWeight,
    TranslateFunc,
    getDimensionUnit,
    formatNumberForDimensions,
    getProductNameFields,
    QuoteEntry,
    ThicknessOption,
    WidthOption,
} from '@steelbuy/ts-shared';
import { getThicknessText } from './ListingUtil';

type ProductNameFieldMap = Record<string, (listing: ListingBuyerModel, t: TranslateFunc) => string>;

export const isRequestQuotePath = (pathname: string) => pathname?.includes('/request-quote/');

const formatDimensionValue = (value: number, t: TranslateFunc): string =>
    t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
        value: formatNumberForDimensions(value),
    });

const productNameFieldMap: ProductNameFieldMap = {
    definition: (listing, t) =>
        'definition' in listing.material && listing.material.definition
            ? t(`domainModel:material.definition.value.${listing.material.definition}`)
            : '',
    dimensions: (listing, t) => {
        const thickness = getThicknessText(
            listing.materialDimensions.thickness,
            listing.materialDimensions.thicknessType,
            t,
            true
        );
        const parts = [thickness, formatDimensionValue(listing.materialDimensions.width, t)];

        if ('length' in listing.materialDimensions && listing.materialDimensions.length) {
            parts.push(formatDimensionValue(listing.materialDimensions.length, t));
        }

        return parts.join(' x ');
    },
    grade: (listing, t) =>
        'grade' in listing.material && listing.material.grade
            ? t(`domainModel:material.grade.value.${listing.material.grade}`)
            : '',
    product: (listing, t) =>
        'product' in listing.material && listing.material.product
            ? t(`domainModel:material.product.value.${listing.material.product}`)
            : '',
    finish: (listing, t) =>
        'finish' in listing.material && listing.material.finish
            ? t(`domainModel:material.finish.value.${listing.material.finish}`)
            : '',
    temper: (listing, t) =>
        'temper' in listing.material && listing.material.temper
            ? t(`domainModel:material.temper.value.${listing.material.temper}`)
            : '',
};

export const buildProductName = (
    listing: ListingBuyerModel,
    t: TranslateFunc,
    overrides: Partial<ProductNameFieldMap> = {}
): string => {
    const fields = getProductNameFields(listing.material) as string[];
    const fieldMap = { ...productNameFieldMap, ...overrides };

    return fields
        .map((field) => fieldMap[field]?.(listing, t) ?? '')
        .filter(Boolean)
        .join(' ');
};

const buildRangeDimensionsText = (entry: QuoteEntry, t: TranslateFunc): string => {
    const { materialDimensions } = entry;

    const thicknessText =
        materialDimensions.thicknessMin !== undefined && materialDimensions.thicknessMax !== undefined
            ? t(`domainModel:material.dimensions.units.${getDimensionUnit()}.rangeLabel`, {
                  min: formatNumberForDimensions(materialDimensions.thicknessMin),
                  max: formatNumberForDimensions(materialDimensions.thicknessMax),
              })
            : formatDimensionValue(materialDimensions.thickness, t);
    const widthText =
        materialDimensions.widthMin !== undefined && materialDimensions.widthMax !== undefined
            ? t(`domainModel:material.dimensions.units.${getDimensionUnit()}.rangeLabel`, {
                  min: formatNumberForDimensions(materialDimensions.widthMin),
                  max: formatNumberForDimensions(materialDimensions.widthMax),
              })
            : formatDimensionValue(materialDimensions.width, t);

    const parts = [thicknessText, widthText];

    if ('length' in materialDimensions && materialDimensions.length) {
        parts.push(formatDimensionValue(materialDimensions.length, t));
    }

    return parts.join(' x ');
};

const isThicknessRange = (entry: QuoteEntry): boolean =>
    entry.materialDimensions.thicknessOption === ThicknessOption.RANGE ||
    (entry.materialDimensions.thicknessMin !== undefined && entry.materialDimensions.thicknessMax !== undefined);

const isWidthRange = (entry: QuoteEntry): boolean =>
    entry.materialDimensions.widthOption === WidthOption.RANGE ||
    (entry.materialDimensions.widthMin !== undefined && entry.materialDimensions.widthMax !== undefined);

export const buildRangeProductName = (entry: QuoteEntry, t: TranslateFunc): string => {
    if (!isThicknessRange(entry) && !isWidthRange(entry)) {
        return buildProductName(entry as ListingBuyerModel, t);
    }

    return buildProductName(entry as ListingBuyerModel, t, {
        dimensions: (listing, translate) => buildRangeDimensionsText(listing, translate),
    });
};

export type FilterInputConfig = Partial<
    Record<
        string,
        {
            getOptions: (material: MaterialProperties, prime?: boolean) => unknown;
            fixedLabelMapper?: (t: TranslateFunc) => string;
            labelMapper?: (option: unknown, t: TranslateFunc) => string;
            valueMapper?: (option: unknown) => string | number | { min: number; max: number };
            sort?: (a: unknown, b: unknown) => number;
        }
    >
>;

/**
 * Material filter fields shared between the marketplace DashboardFilter and
 * the admin RFQFilterPanel. Dashboard-only fields (gauge, thicknessType,
 * quality, location) are intentionally excluded.
 */
export const filterInputs: FilterInputConfig = {
    product: { getOptions: getProducts },
    specification: { getOptions: getSpecifications },
    grade: { getOptions: getGrades },
    gradeFamily: { getOptions: getGradeFamily },
    coatingWeight: { getOptions: getCoatingWeights, sort: sortCoatingWeight as (a: unknown, b: unknown) => number },
    surfaceTreatment: { getOptions: getSurfaceTreatments },
    compliance: { getOptions: getCompliances },
    surfaceFinish: { getOptions: getSurfaceFinishes },
    finish: { getOptions: getFinishes },
    temper: { getOptions: getTempers },
};

/** Fields excluded from material permutation calculation. */
export const nonMaterialFilters = ['gauge', 'location'];
